import * as React from 'react'
import { Command, CommandInput, CommandList, CommandItem } from '../command'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import { Button } from '../button'
import { type ComboboxOptionType, type ComboboxProps } from './single'
import { Checkbox } from '../checkbox'
import { useSelections } from '@/hook/use-selections'
import { isEqual } from 'lodash-es'
import { useContentWidth } from './use-content-width'
import { ComboboxLoading, ComboboxTrigger } from './common'
import { cn } from '@/lib/utils'
import { type Option } from '../cascader/single'
import { HighlightText } from '../highlight-text'

type UseSelections = ReturnType<typeof useSelections<string, ComboboxOptionType>>
export const ComboboxMultiContext = React.createContext<
  Partial<UseSelections> & { onSelect?: (value: string[]) => void; search?: string }
>({})

export function useComboboxMultiContext() {
  return React.useContext(ComboboxMultiContext)
}

function ComboboxMultiOption({ disabled, value, children, keywords = [] }: ComboboxOptionType) {
  const { isSelected, toggle, onSelect, selected, search } = useComboboxMultiContext()
  const display = children
  const keys = keywords.length === 0 && typeof display === 'string' ? [display] : keywords
  return (
    <CommandItem
      disabled={disabled}
      key={value}
      value={value}
      keywords={keys}
      onSelect={() => {
        const newSelected = selected
          ? selected.includes(value)
            ? selected.filter((v) => v !== value)
            : [...selected, value]
          : [value]
        toggle?.(value)
        onSelect?.(newSelected)
      }}
      className={cn('data-[disabled=true]:text-muted-foreground data-[disabled=true]:opacity-100', {
        'bg-accent-select text-accent hover:bg-accent-select hover:text-accent': isSelected?.(value),
      })}
    >
      <div className="flex items-center gap-2 w-full">
        <Checkbox checked={isSelected?.(value)} />
        <label className="flex-1">
          {typeof display === 'string' ? <HighlightText text={display} highlight={search ? [search] : []} /> : children}
        </label>
      </div>
    </CommandItem>
  )
}
function ComboboxMultiSelectAll({ children }: { children: React.ReactNode }) {
  const { selected, setSelected } = useComboboxMultiContext()
  const allValues = React.useMemo(() => {
    const values: string[] = []
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return
      const props = child.props as ComboboxOptionType
      if (!props.disabled && props.value) {
        values.push(props.value)
      }
    })
    return values
  }, [children])

  const checkedStatus = React.useMemo(() => {
    if (allValues.length === 0) return false
    const selectedCount = allValues.filter((v) => selected?.includes(v)).length
    if (selectedCount === 0) return false
    if (selectedCount === allValues.length) return true
    return 'indeterminate'
  }, [selected, allValues])

  return (
    <CommandItem
      value="__select_all__"
      onSelect={() => {
        const newSelected = selected ? (checkedStatus === true ? [] : allValues) : allValues
        setSelected?.(newSelected)
      }}
    >
      <div className="flex items-center gap-2 w-full">
        <Checkbox checked={checkedStatus} />
        <label className="flex-1">全选</label>
        <span className="text-xs text-muted-foreground">
          {selected?.length || 0}/{allValues.length}
        </span>
      </div>
    </CommandItem>
  )
}

export type ComboboxMultiProps = {
  allowSelectAll?: boolean
  value?: string[]
  onValueChange?: (value: string[]) => void
  defaultValue?: string[]
  variant?: 'default' | 'outline'
  customDisplay?: (value: string[], placeholder: string, options: Option[]) => React.ReactNode
  onSelect?: (value: string[]) => void
  trigger?: 'click' | 'hover'
} & Omit<ComboboxProps, 'value' | 'onValueChange' | 'defaultValue'>

function ComboboxMulti({
  placeholder,
  defaultValue,
  searchPlaceholder = '搜索',
  showSearch = true,
  children,
  value,
  onValueChange,
  allowClear = true,
  searchKeyword,
  onSearch,
  className,
  style,
  shouldFilter,
  customDownIcon,
  customDisplay,
  variant,
  onSelect,
  allowSelectAll,
  activeLight = false,
  trigger = 'click',
}: ComboboxMultiProps) {
  const [open, setOpen] = React.useState(false)
  const [composing, setComposing] = React.useState(false)
  const [search, setSearch] = React.useState<string | undefined>(searchKeyword)

  const val = (value ?? defaultValue ?? []) as string[]
  const context = useSelections([], val)
  const { selected: selectedValue, setSelected } = context

  const selected = typeof selectedValue === 'string' ? [] : selectedValue

  // 监听外部value变化，同步更新内部selected状态
  // 只有当下拉菜单未打开时才同步，避免覆盖用户选择
  React.useEffect(() => {
    if (!open && !isEqual(selected, val)) {
      setSelected?.(val)
    }
  }, [val, setSelected, selected, open])
  const $trigger = React.useRef<HTMLButtonElement>(null)
  const contentWidth = useContentWidth($trigger as React.RefObject<HTMLElement>)
  const $list = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    setTimeout(() => {
      if (!$list.current) return
      $list.current.scrollTop = 0
    }, 0)
  }, [children])

  return (
    <ComboboxMultiContext.Provider value={{ ...context, onSelect, search }}>
      <Popover
        open={open}
        onOpenChange={(open) => {
          setOpen(open)
          if (open) {
            setSelected?.(val)
          }
        }}
      >
        <PopoverTrigger asChild>
          <ComboboxTrigger
            role="combobox"
            ref={$trigger}
            allowClear={allowClear}
            selected={val.length > 0}
            activeLight={activeLight}
            onClear={() => onValueChange?.([])}
            className={className}
            aria-expanded={open}
            style={style}
            customDownIcon={customDownIcon}
            variant={variant}
            onMouseEnter={() => {
              if (trigger === 'hover') {
                setOpen(true)
              }
            }}
            onMouseLeave={() => {
              if (trigger === 'hover') {
                setOpen(false)
              }
            }}
          >
            {customDisplay ? (
              customDisplay(val, placeholder || '', [])
            ) : val.length > 0 ? (
              <div className="flex gap-1">
                {placeholder}
                <div className="px-2 flex items-center text-center bg-accent text-background rounded-xl">{val.length}</div>
              </div>
            ) : (
              placeholder || '不限'
            )}
          </ComboboxTrigger>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-full p-0"
          style={{ minWidth: contentWidth }}
          onMouseEnter={() => {
            if (trigger === 'hover') {
              setOpen(true)
            }
          }}
          onMouseLeave={() => {
            if (trigger === 'hover') {
              setOpen(false)
            }
          }}
        >
          <Command shouldFilter={shouldFilter}>
            <div className="p-2">
              <div
                className={cn('flex items-center gap-2', {
                  'mb-2': showSearch,
                })}
              >
                {showSearch && (
                  <CommandInput
                    value={search}
                    onValueChange={(v) => {
                      setSearch(v)
                      if ($list.current) {
                        $list.current.scrollTop = 0
                      }
                      if (composing) return
                      onSearch?.(v)
                    }}
                    onCompositionStart={() => {
                      setComposing(true)
                    }}
                    onCompositionEnd={(ev) => {
                      setComposing(false)
                      onSearch?.((ev.target as HTMLInputElement).value)
                    }}
                    placeholder={searchPlaceholder}
                    className="h-9"
                  />
                )}
              </div>
              <div className="flex">
                <CommandList ref={$list} className="min-w-52 max-w-120">
                  {allowSelectAll && <ComboboxMultiSelectAll>{children}</ComboboxMultiSelectAll>}
                  <React.Suspense fallback={<ComboboxLoading />}>{children}</React.Suspense>
                </CommandList>
              </div>
            </div>
            <div className="py-3 px-4 flex items-center justify-end gap-2 border-t border-active">
              <Button
                className="w-20 h-8"
                onClick={() => {
                  setSelected([])
                }}
                variant="secondary"
              >
                清空
              </Button>
              <Button
                disabled={allowClear === false && selected?.length === 0}
                onClick={() => {
                  if (!isEqual(val, selected)) {
                    onValueChange?.(selected)
                  }
                  setOpen(false)
                }}
                className="w-20 h-8"
              >
                确定
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </ComboboxMultiContext.Provider>
  )
}

ComboboxMulti.Option = ComboboxMultiOption
export default ComboboxMulti
